// @flow
import Href from "../href/Href";

import type { MessageParameters } from "../../i18n/types";
import LayoutHintCollection from "../layouthint/LayoutHintCollection";
import { getBasePathModularUI } from "../../constants";
import type { ModelOptions } from "../types";

/**
 */
export type TitleObject = {
  +id: string,
  +properties?: Object,
  +message?: string,
};
/**
 */
export type RedirectTextObject = {
  +id: string,
  +properties?: Object,
  +message?: string,
};

/**
 */
export type RedirectObject = {
  +href: Href,
  +text?: RedirectTextObject,
};

/**
 * Error response model
 */
export default class ErrorResponse {
  _error: Object;
  _connectKey: ?string;

  _modelOptions: ?ModelOptions;

  /**
   * Construct ErrorResponse
   */
  constructor(data: Object, connectKey?: string, modelOptions?: ModelOptions) {
    this._error = data;
    this._connectKey = connectKey;

    this._modelOptions = modelOptions;
  }

  /**
   */
  set connectKey(key: string) {
    this._connectKey = key;
  }

  /**
   */
  get connectKey(): ?string {
    return this._connectKey;
  }

  /**
   */
  get origin(): string {
    return this._modelOptions?.origin || "";
  }

  /**
   */
  get contextPath(): string {
    return this._modelOptions?.contextPath || getBasePathModularUI();
  }

  /**
   * Return error information
   */
  get error(): Object {
    return this._error ?? {};
  }

  /**
   * Get request status code
   */
  get status(): number {
    const NO_RESPONSE_CODE = 0;
    const DECIMAL_RADIX = 10;
    return this.error.status
      ? parseInt(this.error.status, DECIMAL_RADIX)
      : NO_RESPONSE_CODE;
  }

  /**
   * Return error type information
   */
  get id(): string {
    if (this.isResourceNotFound && this.error.id !== "Error.NotAuthorized") {
      return "Error.ResourceNotFound";
    }

    return this.error.id || this.error.name || "Error.GeneralError";
  }

  /**
   */
  get message(): string {
    return this.error.message || this.id;
  }

  /**
   * Return response error message
   */
  get responseMessage(): string {
    return this.response.message || null;
  }

  /**
   *  Return response error title
   */
  get title(): TitleObject {
    return this.response.title || null;
  }

  /**
   * Return response error title message
   */
  get titleMessage(): string {
    return this.response.title?.message || undefined;
  }

  /**
   * Return response error title Id
   */
  get titleId(): string {
    return this.response.title?.id || undefined;
  }

  /**
   *  Return response error title properties
   */
  get titleProperties(): Object {
    return this.response.title?.properties || null;
  }

  /**
   *  Return response error redirect
   */
  get redirect(): RedirectObject {
    return this.response.redirect || null;
  }
  /**
   *  Return response error redirect text
   */
  get redirectText(): RedirectTextObject {
    return this.response?.redirect?.text || null;
  }

  /**
   * Return response error redirect text id
   */
  get redirectTextID(): string {
    return this.response.redirect?.text?.id || undefined;
  }

  /**
   *  Return response error redirect text properties
   */
  get redirectTextProperties(): Object {
    return this.response.redirect?.text?.properties || null;
  }

  /**
   * Return response error redirect text message
   */
  get redirectMessage(): string {
    return this.response.redirect?.text?.message || undefined;
  }

  /**
   *  Return response error redirect href
   */
  get redirectHref(): Href {
    if (this.response.redirect?.href) {
      return new Href(this.response.redirect?.href, "", this._modelOptions);
    }
    return new Href();
  }

  /**
   * Return error response layouthint
   */
  get layouthint(): LayoutHintCollection {
    return new LayoutHintCollection(this.response?.layouthint);
  }

  /**
   */
  get response(): Object {
    return this.error?.response?.error || {};
  }

  /**
   */
  get properties(): Object {
    const { properties } = this.error;
    if (properties != null && typeof properties == "object") {
      return properties;
    }

    return {};
  }

  /**
   * Return error parameters
   */
  get parameters(): ?MessageParameters {
    return this.error.parameters || null;
  }

  /**
   */
  get isResourceNotFound(): boolean {
    const RESOURCE_NOT_FOUND_RESPONSE_CODE = 404;

    return this.status === RESOURCE_NOT_FOUND_RESPONSE_CODE;
  }

  /**
   */
  get isResourceNotFoundAfterReload(): boolean {
    return this.isResourceNotFound && this.error.isReload;
  }

  /**
   * Check if the error message is an authorization error
   */
  get isUnauthorized(): boolean {
    const UNAUTHORIZED_RESPONSE_CODE = 401;

    const hasUnauthorizedStatus = this.status === UNAUTHORIZED_RESPONSE_CODE;
    const hasUnauthorizedErrorId =
      this.id === "Error.NotAuthorized" ||
      this.id === "Error.Authentication.Required" ||
      this.id === "Error.Authentication.InvalidCredentials";

    const hasLoginAction = this.error.action
      ? this.error.action.name === "login"
      : false;

    return hasUnauthorizedStatus || hasUnauthorizedErrorId || hasLoginAction;
  }

  /**
   */
  get isChangePassword(): boolean {
    return this.id === "Error.ChangePasswordRequired";
  }

  /**
   */
  get isConcurrentUser(): boolean {
    return this.id === "Error.Authentication.ConcurrentUser";
  }

  /**
   */
  get isConcurrentError(): boolean {
    return (
      this.id === "Error.Case.ConcurrentModification" ||
      this.id === "Error.DataStore.ConcurrentModification" ||
      this.id === "Error.DataStore.RecordAlreadyExists" ||
      this.id === "Error.DataStore.RowCannotBeLocked"
    );
  }

  /**
   */
  get isBlocked(): boolean {
    return this.id === "Error.Authentication.BlockedUser";
  }

  /**
   */
  get isInvalidCredentials(): boolean {
    return this.id === "Error.Authentication.InvalidCredentials";
  }

  /**
   */
  get isInvalidUsername(): boolean {
    return this.id === "Error.Authentication.InvalidUsername";
  }

  /**
   */
  get isTimeoutError(): boolean {
    return this.id === "Error.CodemapLookup.InvalidToken";
  }

  /**
   * A RemoteServiceException occurs when an exception occurs connecting to an external system,
   * for example when using the service list panels
   */
  get isRemoteServiceException(): boolean {
    return this.id === "Error.RemoteServiceException";
  }

  /**
   * Get response url
   */
  get changePasswordHref(): ?Href {
    return this.properties.redirect
      ? new Href(this.properties.redirect, "", this._modelOptions)
      : null;
  }

  /**
   * When no action information is present in the unauthorized response, for now we assume it is Basic Authentication
   */
  get isBasicAuthentication(): boolean {
    const UNAUTHORIZED_RESPONSE_CODE = 401;
    return (
      this.isUnauthorized &&
      this.status === UNAUTHORIZED_RESPONSE_CODE &&
      this.response.error === "No responseText"
    );
  }

  /**
   * Retrieve a failed login attempt
   */
  get loginFailed(): boolean {
    return (
      this.id === "Error.Authentication.Required" ||
      this.id === "Error.Authentication.InvalidCredentials"
    );
  }

  /**
   */
  get shouldThrowOnServer(): boolean {
    return !this.isChangePassword && !this.isBlocked && !this.isConcurrentUser;
  }
}
